import { assetRelationTypes, type AssetRelationType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import LoadingAsset from '~/components/assets/LoadingAsset'
import { PropertyListItem } from '~/components/assets/PropertyListItem.tsx'
import {
  PropertyTypeFilter,
  type PropertyTypeFilterOption,
} from '~/components/assets/PropertyTypeFilter'
import { InviteTeamMemberForm } from '~/components/home/InviteTeamMemberForm'
import { SectionContent } from '~/components/layout/Sections.tsx'
import { Body } from '~/components/typography/Content'
import { Heading1, Heading2 } from '~/components/typography/Titles'
import { useIsBottomSheetOpen } from '~/components/ui/bottom-sheet'
import { Button, LinkButton } from '~/components/ui/button.tsx'
import { useUserAssets, useUserOwnAssets } from '~/data/assets/useAssets'
import { useFilterTabCounts } from '~/lib/useFilterTabCounts'
import { cn } from '~/lib/utils.ts'

export function PropertyListingView({
  className,
  relationType,
}: {
  className?: string
  relationType?: AssetRelationType
}) {
  const { t } = useTranslations(['property', 'common', 'home'])
  const [selectedRelation, setSelectedRelation] = useState<AssetRelationType>(
    relationType ?? 'prospect'
  )

  useEffect(() => {
    if (relationType) {
      setSelectedRelation(relationType as AssetRelationType)
    }
  }, [relationType])

  const currentRelation: AssetRelationType = selectedRelation

  const { totalCounts, fetchAllCounts, mergeTotalCounts } = useFilterTabCounts<AssetRelationType>({
    filterKeys: assetRelationTypes,
  })

  const relationTypesForOrganizationAssets =
    currentRelation === 'reference'
      ? fetchAllCounts
        ? (['prospect'] as AssetRelationType[])
        : []
      : [currentRelation]

  const {
    assets: allAssets,
    totalCounts: totalCountsFromApi,
    isLoading: allAssetsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUserAssets({
    relationTypes: relationTypesForOrganizationAssets,
    includeOrganization: true,
    fetchAllCounts,
  })

  const { assets: ownAssets, isLoading: ownAssetsLoading } = useUserOwnAssets({
    relationTypes: [currentRelation],
    isEnabled: currentRelation === 'reference',
  })

  const assets = useMemo(() => {
    if (currentRelation === 'reference') {
      return ownAssets
    }

    return allAssets
  }, [currentRelation, ownAssets, allAssets])

  useEffect(() => {
    mergeTotalCounts(totalCountsFromApi)
  }, [mergeTotalCounts, totalCountsFromApi])

  const isLoading = currentRelation === 'reference' ? ownAssetsLoading : allAssetsLoading

  const router = useRouter()
  const isInactive = useIsBottomSheetOpen()

  const filterOptions = useMemo<PropertyTypeFilterOption[]>(
    () => [
      {
        key: 'prospect',
        label: t(($) => $.listing.prospects),
        count: totalCounts.prospect,
      },
      {
        key: 'representation',
        label: t(($) => $.listing.salesListings),
        count: totalCounts.representation,
      },
      {
        key: 'reference',
        label: t(($) => $.listing.soldProperties),
        count: totalCounts.reference,
      },
    ],
    [t, totalCounts]
  )

  return (
    <View
      className={cn(
        'gap-6 px-2 sm:px-3',
        isInactive && 'pointer-events-none opacity-50',
        className
      )}>
      <View className="gap-6">
        <View className="flex-row items-center justify-evenly gap-3 pl-4">
          <Heading1 className="flex-1">{t(($) => $.listing.allAssetsHeader)}</Heading1>
          <LinkButton href="/(authenticated)/properties" variant="link" size="sm">
            {t(($) => $.listing.manageProperties)}
          </LinkButton>
        </View>
        <PropertyTypeFilter
          options={filterOptions}
          selected={currentRelation}
          onChange={(key) => setSelectedRelation(key as AssetRelationType)}
        />
      </View>
      <View className="flex-1 gap-3">
        {isLoading ? <LoadingAsset className="mb-6" /> : null}
        {(!assets || assets.length === 0) && currentRelation === 'prospect' && (
          <View className="native:flex-1 gap-4 py-8">
            <Heading2 className="text-center">
              {t(($) => $.realtor.inviteTeam.emptyStateProspects.title)}
            </Heading2>
            <Body className="text-center text-base/normal text-primary">
              {t(($) => $.realtor.inviteTeam.emptyStateProspects.description)}
            </Body>
            <InviteTeamMemberForm className="px-2" />
          </View>
        )}
        {(!assets || assets.length === 0) && currentRelation !== 'prospect' && (
          <SectionContent
            textClassName="text-center text-xl text-primary/70"
            className="native:flex-1 py-8">
            {currentRelation === 'reference'
              ? t(($) => $.listing.noReferences)
              : t(($) => $.listing.noSalesListings)}
          </SectionContent>
        )}
        {assets?.map((item) => (
          <PropertyListItem
            key={item.id}
            asset={item}
            organisationName={item.organisation?.name}
            realtorName={item.organisation?.realtorName}
            actions={[
              {
                key: 'primary',
                label: t(($) => $.actions.open),
                onPress: () => router.push(`/property/${item.id}`),
              },
            ]}
          />
        ))}

        {hasNextPage && currentRelation !== 'reference' && (
          <Button
            variant="outline"
            onPress={() => fetchNextPage()}
            loading={isFetchingNextPage}
            className="mt-3 self-center">
            {t(($) => $.common.loadMore)}
          </Button>
        )}
      </View>
    </View>
  )
}
